import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Plus } from "lucide-react";
import { api, formatApiError } from "../../api";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { toast } from "../../components/ui/sonner";
import { ErrorState } from "../../components/common/ErrorState";
import { CreateKairooRoleDialog } from "../../components/control/CreateKairooRoleDialog";
import { CreateKairooUserDialog } from "../../components/control/CreateKairooUserDialog";
import { EditRolePermissionsDialog } from "../../components/control/EditRolePermissionsDialog";
import { STAFF } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

// UX-CP-02 §2 SCR-P08-40/41 "Kairoo staff & RBAC" — additive backend this session (Prompt 00-B
// never delivered it). Requires `staff`.
export default function KairooStaffPage() {
  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();

  const rolesQ = useQuery({ queryKey: ["kairoo-roles"], queryFn: async () => (await api.get("/control/kairoo-roles")).data.roles });
  const usersQ = useQuery({ queryKey: ["kairoo-users"], queryFn: async () => (await api.get("/control/kairoo-users")).data.users });

  const reassignMutation = useMutation({
    mutationFn: ({ userId, roleRef }) => api.patch(`/control/kairoo-users/${userId}/role`, { kairoo_role_ref: roleRef }),
    onSuccess: () => {
      toast.success("Role reassigned");
      queryClient.invalidateQueries({ queryKey: ["kairoo-users"] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  if (rolesQ.isError || usersQ.isError) {
    return <ErrorState error={rolesQ.error || usersQ.error} onRetry={() => { rolesQ.refetch(); usersQ.refetch(); }} title="Couldn't load Kairoo Staff" />;
  }

  return (
    <div data-testid={STAFF.page} className="space-y-6 max-w-4xl pb-8">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }}>Kairoo Staff</h1>
        <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Manage platform operators and the roles that gate what they can touch.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="solid-surface rounded-[14px] overflow-hidden">
        <div className="flex items-center justify-between p-5 pb-3">
          <p className="eyebrow">Roles</p>
          <Button data-testid={STAFF.createRoleButton} size="sm" onClick={() => setCreateRoleOpen(true)} className="gap-1.5 text-white" style={{ background: "var(--scarlet-accent)" }}><Plus size={14} /> New role</Button>
        </div>
        <Table data-testid={STAFF.rolesTable}>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Permissions</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {(rolesQ.data || []).map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium" style={{ color: "var(--scarlet-text)" }}>{r.name}</TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{r.permissions.join(", ") || "—"}</TableCell>
                <TableCell>
                  <Button data-testid={STAFF.editPermissionsButton(r.id)} size="sm" variant="outline" onClick={() => setEditingRole(r)}>Edit permissions</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="solid-surface rounded-[14px] overflow-hidden">
        <div className="flex items-center justify-between p-5 pb-3">
          <p className="eyebrow">Staff</p>
          <Button data-testid={STAFF.createUserButton} size="sm" onClick={() => setCreateUserOpen(true)} className="gap-1.5 text-white" style={{ background: "var(--scarlet-accent)" }}><Plus size={14} /> Add staff</Button>
        </div>
        <Table data-testid={STAFF.usersTable}>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead></TableRow></TableHeader>
          <TableBody>
            {(usersQ.data || []).map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-medium" style={{ color: "var(--scarlet-text)" }}>{u.name}</TableCell>
                <TableCell style={{ color: "var(--scarlet-text-secondary)" }}>{u.email}</TableCell>
                <TableCell>
                  <Select value={u.kairoo_role_ref} onValueChange={(roleRef) => reassignMutation.mutate({ userId: u.id, roleRef })}>
                    <SelectTrigger data-testid={STAFF.reassignRoleSelect(u.id)} className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(rolesQ.data || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </motion.div>

      <CreateKairooRoleDialog open={createRoleOpen} onOpenChange={setCreateRoleOpen} />
      <CreateKairooUserDialog roles={rolesQ.data} open={createUserOpen} onOpenChange={setCreateUserOpen} />
      <EditRolePermissionsDialog role={editingRole} open={!!editingRole} onOpenChange={(v) => !v && setEditingRole(null)} />
    </div>
  );
}
